import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MolecularProperties } from '@peruselab/structure-editor/chemistry/chemicalValidation';
import { Icon } from '../../components/Icon';
import { DrawingInfoPanel, type DrawingProblem } from '../../components/panels/DrawingInfoPanel';
import type { StructureChecksActions } from '../../components/panels/StructureChecksSection';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';
import { structureInfoAutoOpenPreference } from '../../preferences/displayPreferences';
import { usePreference } from '../../preferences/storedPreference';

interface FeedbackDisclosureProps {
  readonly metrics: { readonly nodes: number; readonly edges: number; readonly atoms: number; readonly bonds: number };
  readonly hasGraphNodes: boolean;
  readonly problems: readonly DrawingProblem[] | null;
  readonly properties: MolecularProperties | null;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly onShowChecks: () => void;
  readonly actions: Omit<StructureChecksActions, 'onSelect'>;
}

// #480 — Graph stats and Chemistry validation are low-frequency "how's my structure doing?"
// info, collapsed behind one combined toggle (not two: both read together at a glance, and one
// icon keeps the toolbar less cluttered). An anchored popover, not a full-screen modal like
// FileDrawer/PeriodicTablePanel -- checking stats is meant to be a quick glance while the canvas
// stays visible and interactive, not a deliberate takeover task. Same disclosure pattern as
// AccountMenu.tsx: outside-click/Escape close, focus returns to the trigger.
//
// Positioned with `position: fixed` via useAnchoredPopover (viewport coordinates computed from
// the trigger's own boundingClientRect), not plain CSS `position: absolute` -- the toggle lives
// in `.workspace-toolbar`, inside `.workspace-center`, which has `overflow: auto` for its own
// internal scrolling (ADR 0007's three-column shell). An absolutely-positioned popover wide
// enough to reach past that column's right edge gets clipped there, which reads as "hidden
// behind the right rail". `position: fixed` escapes that clipping entirely since no ancestor
// here sets `transform`/`filter`/`will-change` (which would otherwise create a containing block
// fixed positioning can't escape).
//
// #516 -- the panel's width (`min(420px, calc(100vw - 2rem))`, mirrored below as the estimate
// useAnchoredPopover needs before it can measure the real thing) is nearly the full viewport on a
// narrow phone. The toggle doesn't have to reach the viewport's absolute right edge for a panel
// that wide to still overflow the *left* edge with a naive right-anchor -- there's a
// panel-collapse chevron after it in the toolbar. useAnchoredPopover clamps against both edges.
const FEEDBACK_PANEL_MAX_WIDTH_PX = 420;
const FEEDBACK_PANEL_VIEWPORT_INSET_PX = 32; // 2rem, matching the CSS `calc(100vw - 2rem)`

/**
 * #1110 P6: counts up each time a problem appears that wasn't open a moment ago. Problems already seen don't
 * count again, so a panel closed on them stays closed; once the paper is known to be clean, the
 * slate is wiped. Null problems (not known yet) change nothing.
 */
export function useNewProblemSignal(problems: readonly DrawingProblem[] | null): number {
  const seen = useRef(new Set<string>());
  const [signal, setSignal] = useState(0);
  useEffect(() => {
    if (problems === null) return;
    if (problems.length === 0) {
      seen.current = new Set();
      return;
    }
    const fresh = problems.some((problem) => !seen.current.has(problem.key));
    for (const problem of problems) seen.current.add(problem.key);
    if (fresh) setSignal((count) => count + 1);
  }, [problems]);
  return signal;
}

export function FeedbackDisclosure({
  metrics,
  hasGraphNodes,
  problems,
  properties,
  failed,
  onRetry,
  onShowChecks,
  actions
}: FeedbackDisclosureProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const autoOpen = usePreference(structureInfoAutoOpenPreference);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const anchor = useAnchoredPopover(open, triggerRef, panelRef, () =>
    Math.min(FEEDBACK_PANEL_MAX_WIDTH_PX, window.innerWidth - FEEDBACK_PANEL_VIEWPORT_INSET_PX)
  );
  useDismissableOverlay(open, setOpen, triggerRef, panelRef);

  // P6/P7: a new problem opens the panel -- without moving the focus, so drawing goes on -- unless
  // the reader turned that off, or the icon isn't on screen (the compact top bar keeps it in its
  // own collapsed panel; on a phone only the badge tells).
  const newProblem = useNewProblemSignal(problems);
  useEffect(() => {
    if (newProblem === 0 || !autoOpen) return;
    const trigger = triggerRef.current;
    if (trigger && (trigger.checkVisibility?.() ?? true)) setOpen(true);
  }, [newProblem, autoOpen]);

  const problemCount = problems?.length ?? 0;
  const label =
    problemCount > 0
      ? t('workspace.feedback.toggleWithProblems', { count: problemCount })
      : t('workspace.feedback.toggle');
  const hasError = problems?.some((problem) => problem.severity === 'ERROR') ?? false;

  return (
    <div className="workspace-feedback-disclosure">
      <button
        ref={triggerRef}
        type="button"
        className="icon-button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        title={label}
        onClick={() => setOpen((value) => !value)}
        data-testid="feedback-toggle"
      >
        <Icon name="monitoring" />
        {problemCount > 0 ? (
          <span
            className={`workspace-feedback-badge${hasError ? '' : ' workspace-feedback-badge--warning'}`}
            aria-hidden="true"
            data-testid="feedback-badge"
          >
            {problemCount}
          </span>
        ) : null}
      </button>
      {open && anchor ? (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={t('workspace.feedback.panelLabel')}
          className={`workspace-feedback-panel${problemCount > 0 ? ' workspace-feedback-panel--problem' : ''}`}
          style={{ top: anchor.top, left: anchor.left }}
          data-testid="feedback-panel"
        >
          <DrawingInfoPanel
            metrics={metrics}
            hasGraphNodes={hasGraphNodes}
            problems={problems}
            properties={properties}
            failed={failed}
            onRetry={onRetry}
            onShowChecks={() => {
              setOpen(false);
              onShowChecks();
            }}
            actions={actions}
            autoOpen={autoOpen}
            onAutoOpenChange={structureInfoAutoOpenPreference.set}
            onClose={() => {
              setOpen(false);
              triggerRef.current?.focus();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
